import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';

export interface UploadFileParams {
  object: string;
  object_id: string;
  file_tag: string;
  file: File;
}

export interface UploadMultipleFilesParams {
  object: string;
  object_id: string;
  file_tag: string;
  files: File[];
}

export interface UploadFileResponse {
  success: boolean;
  message?: string;
  uploads_files_ids?: string[];
  file_keys?: string[];
  file_tags?: string[];
  file_names?: string[];
  file_names2?: string[];
  file_types?: string[];
  link?: string;
  links?: string[];
  url_avatar?: string | null;
  current_server_time?: string;
}

export interface FileItem {
  id: string;
  object: string;
  object_id: string;
  uploader_object: string;
  uploader_id: string;
  hash: string;
  type: string;
  file: string;
  name: string;
  tag: string;
  notes: string;
  created: string;
  scanner_status: number;
  size: number;
  metadata: any;
  c__d__name: string;
  c__d__created: string;
  c__d__type: string;
  c__d__fullname: string | null;
  _highlighted: boolean;
  _highlightmap: Record<string, any>;
  thumbnail: string;
}

export interface FetchFilesResponse {
  success: boolean;
  upload_files: {
    results: FileItem[];
    totalCount: number;
  };
  current_server_time: string;
}

export interface DeleteFileResponse {
  success: boolean;
  message?: string;
  current_server_time?: string;
}

/**
 * Composable that provides access to Files API functions.
 */
export function useFilesAPI() {
  const authStore = useAuthStore();
  const httpClient = getHttpClient(authStore);

  return {
    async uploadFile(params: UploadFileParams, onProgress?: (progress: number) => void): Promise<UploadFileResponse> {
      try {
        // Create FormData object
        const formData = new FormData();

        // Add file metadata
        formData.append('object', params.object);
        formData.append('object_id', params.object_id);
        formData.append('file_tag', params.file_tag);

        // Add the file
        formData.append('fileAttachment[]', params.file, params.file.name);

        // Use httpClient for file upload with optional progress tracking
        // The httpClient will automatically handle CSRF tokens and headers
        const data = await httpClient.post<UploadFileResponse>(
          'admin/v4/files/',
          formData,
          { sAction: 'putFile' },
          undefined, // headers
          onProgress // progress callback
        );

        if (data.success) {
          return data;
        }

        throw new Error(data.message || 'Failed to upload file');
      } catch (error) {
        console.error('FilesAPI: Error uploading file:', error);
        throw error;
      }
    },

    async uploadMultipleFiles(params: UploadMultipleFilesParams, onProgress?: (progress: number) => void): Promise<UploadFileResponse> {
      try {
        // Create FormData object
        const formData = new FormData();

        // Add file metadata
        formData.append('object', params.object);
        formData.append('object_id', params.object_id);

        // Add each file with its own file_tag
        // Based on the API example, we need to add file_tag before each fileAttachment[]
        params.files.forEach((file) => {
          formData.append('file_tag', params.file_tag);
          formData.append('fileAttachment[]', file, file.name);
        });

        // Use httpClient for file upload with optional progress tracking
        // The httpClient will automatically handle CSRF tokens and headers
        const data = await httpClient.post<UploadFileResponse>(
          'admin/v4/files/',
          formData,
          { sAction: 'putFile' },
          undefined, // headers
          onProgress // progress callback
        );

        if (data.success) {
          return data;
        }

        throw new Error(data.message || 'Failed to upload files');
      } catch (error) {
        console.error('FilesAPI: Error uploading multiple files:', error);
        throw error;
      }
    },

    async fetchFiles(
      objectType: string, 
      objectId: string,
      options?: {
        page?: number;
        limit?: number;
        query?: string; // Note: Backend doesn't currently support this parameter (as of Oct 2025)
      }
    ): Promise<{ files: FileItem[]; totalCount: number }> {
      const page = options?.page || 1;
      const limit = options?.limit || 25;
      const start = (page - 1) * limit;
      
      const data = await httpClient.get<FetchFilesResponse>('admin/v4/files/', {
        'sAction': 'listing',
        'includeChecklistsFormUploads': 'true',
        'page': String(page),
        'start': String(start),
        'limit': String(limit),
        // Note: Backend doesn't currently support query parameter, but keeping it for future compatibility
        ...(options?.query ? { 'query': options.query } : {}),
        'filter': JSON.stringify([
          { property: 'object', value: objectType },
          { property: 'object_id', value: objectId },
          { property: 'tag', value: null }
        ])
      });
      
      if (data.success && data.upload_files?.results) {
        return {
          files: data.upload_files.results,
          totalCount: data.upload_files.totalCount || 0
        };
      }
      
      return {
        files: [],
        totalCount: 0
      };
    },

    async deleteFile(fileId: string): Promise<DeleteFileResponse> {
      try {
        // Create FormData object (same pattern as uploadFile)
        const formData = new FormData();
        formData.append('includeChecklistsFormUploads', 'true');
        formData.append('upload_files', JSON.stringify([{ id: fileId }]));

        const data = await httpClient.post<DeleteFileResponse>(
          'admin/v4/files/',
          formData,
          { sAction: 'delete' },
          undefined // No explicit headers - let httpClient handle it like uploadFile
        );

        if (data.success) {
          return data;
        }

        throw new Error(data.message || 'Failed to delete file');
      } catch (error) {
        console.error('FilesAPI: Error deleting file:', error);
        throw error;
      }
    },

    /**
     * Uploads an avatar image for a customer/member
     * @param customerId - The customer/member ID
     * @param file - The image file (usually a cropped blob converted to File)
     * @param onProgress - Optional progress callback
     * @returns Promise with upload response including avatar URL
     */
    async uploadCustomerAvatar(customerId: string, file: File, onProgress?: (progress: number) => void): Promise<UploadFileResponse> {
      return this.uploadFile({
        object: 'members',
        object_id: customerId,
        file_tag: 'avatar',
        file
      }, onProgress);
    },

    async uploadContactAvatar(contactId: string, file: File, onProgress?: (progress: number) => void): Promise<UploadFileResponse> {
      return this.uploadFile({
        object: 'members_users',
        object_id: contactId,
        file_tag: 'avatar',
        file
      }, onProgress);
    },

    /**
     * Download a file by URL and return it as a File object
     * This method handles authentication and CORS properly through httpClient
     */
    async downloadFileFromUrl(url: string, fileName: string, mimeType: string): Promise<File> {
      try {
        // Parse the URL to extract file ID or path if it's an API URL
        // For upload_files URLs, we can fetch directly but need proper authentication
        
        // Use XMLHttpRequest with proper auth headers through a different approach
        // Since httpClient doesn't support blob responses directly, we'll use XHR
        // but get the CSRF token from the auth store
        const authStore = useAuthStore();
        
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('GET', url, true);
          xhr.responseType = 'blob';
          xhr.withCredentials = true;
          
          // Add all necessary headers from httpClient
          xhr.setRequestHeader('x-boomtown-csrf-token', authStore.csrfToken || '');
          xhr.setRequestHeader('content-type', 'application/json');
          
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              const blob = xhr.response as Blob;
              const file = new File([blob], fileName, { type: mimeType || blob.type });
              resolve(file);
            } else {
              reject(new Error(`Failed to download file: ${xhr.status} ${xhr.statusText}`));
            }
          };
          
          xhr.onerror = () => {
            reject(new Error('Network error while downloading file'));
          };
          
          xhr.send();
        });
      } catch (error) {
        console.error('FilesAPI: Error downloading file:', error);
        throw error;
      }
    }
  };
} 